<script setup lang="ts">
import ScriptedExtensionDemo from "../components/ScriptedExtensionDemo.vue";
import { runtimeConfig } from "../api/config";

const features = [
  { title: "按类型整理", description: "分别查看商品主图、视频、SKU 图和详情图。", path: "M4 7.5h6m4 0h6M4 12h6m4 0h6M4 16.5h6m4 0h6" },
  { title: "逐项复核", description: "进入每一类查看素材，按实际需要保留或取消选择。", path: "M4.5 12.5 9 17l10.5-11M5 6.5h6M5 11h3M13.5 17H19" },
  { title: "下载选中素材", description: "人工复核后在浏览器本地生成 ZIP；素材较多时自动分包。", path: "M12 4v10m0 0 4-4m-4 4-4-4M5 18.5h14" },
] as const;

const steps = [
  ["打开商品页", "进入要整理的 1688 商品详情页，插件开始检测素材。"],
  ["查看并选择", "按主图、视频、SKU 图和详情图逐类检查素材。"],
  ["下载素材包", "把确认保留的素材和商品信息保存到本地。"],
] as const;
</script>

<template>
  <section class="hero page-width">
    <div class="hero-copy">
      <h1>整理并下载 1688 商品素材</h1>
      <p>打开商品页，自动识别主图、视频、SKU 图和详情图。逐类复核后，一次下载为结构清晰的本地素材包。</p>
      <div class="hero-actions">
        <a v-if="runtimeConfig.extensionInstallUrl" class="primary-action" :href="runtimeConfig.extensionInstallUrl">安装浏览器扩展</a>
        <a v-else class="primary-action" href="#how-it-works">查看操作步骤</a>
        <a class="secondary-action" href="#features">了解整理能力</a>
      </div>
      <p v-if="!runtimeConfig.extensionInstallUrl" class="availability-note">Chrome / Edge 安装入口暂未开放</p>
      <a v-else class="availability-note availability-note--link" :href="runtimeConfig.extensionInstallUrl">前往扩展安装页</a>
    </div>
    <ScriptedExtensionDemo />
  </section>

  <section id="features" class="features page-width">
    <header class="section-heading"><h2>整理素材的三个步骤</h2><p>插件负责识别和归类，你确认最终选择。</p></header>
    <div class="feature-list">
      <article v-for="feature in features" :key="feature.title">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="feature.path" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" /></svg>
        <h3>{{ feature.title }}</h3>
        <p>{{ feature.description }}</p>
      </article>
    </div>
  </section>

  <section id="how-it-works" class="how-it-works">
    <div class="page-width">
      <header class="section-heading"><h2>从商品页到本地文件</h2></header>
      <ol>
        <li v-for="(step, index) in steps" :key="step[0]"><span>{{ index + 1 }}</span><div><h3>{{ step[0] }}</h3><p>{{ step[1] }}</p></div></li>
      </ol>
    </div>
  </section>

  <section class="faq page-width">
    <header class="section-heading"><h2>常见问题</h2><p>关于素材识别、选择和本地下载。</p></header>
    <div>
      <details><summary>使用插件需要登录吗？</summary><p>不需要。素材识别、选择和 ZIP 下载都在浏览器本地完成。</p></details>
      <details><summary>插件会自动删除素材吗？</summary><p>不会。检测到的素材默认交给你复核，最终下载内容由当前选择决定。</p></details>
      <details><summary>素材会怎样分类？</summary><p>插件按 1688 商品页结构整理为商品主图、商品视频、SKU 图和详情图。</p></details>
      <details><summary>素材较多怎么办？</summary><p>下载内容较多时会自动分包，并保留统一的目录结构和商品信息。</p></details>
    </div>
  </section>
</template>
